import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { Spin, Input } from "antd";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { toast } from "react-toastify";
import {
  getAllAttendance,
  postAttendanceSignIn,
  putAttendanceSignOut,
  AttendanceRecord,
  getAttendanceWorkingHours,
  AttendanceWidgetStats,
  getAttendanceWidgets,
} from "../../../core/api/useAttendance";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import { fetchEmployeeImage } from "../../../core/api/fetchEmployeeImage";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Dropdown } from "react-bootstrap";

const { Search } = Input;

const AttendanceAdmin = () => {
  const employeeIdFromState = useSelector(
    (state: RootState) => state.auth.employeeId
  );

  const employeeId = (() => {
    if (!employeeIdFromState || employeeIdFromState === "NaN") return null;
    if (
      typeof employeeIdFromState === "string" &&
      /^[A-Za-z]/.test(employeeIdFromState)
    ) {
      return employeeIdFromState;
    }
    const numericValue = Number(employeeIdFromState);
    return !isNaN(numericValue) ? numericValue : employeeIdFromState;
  })();

  const name = useSelector((state: RootState) => state.auth.name);
  const empToken = useSelector((state: any) => state.auth.token);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [signedIn, setSignedIn] = useState(false);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [filteredData, setFilteredData] = useState<AttendanceRecord[]>([]);
  const [weekHours, setWeekHours] = useState<string>("0");
  const [monthHours, setMonthHours] = useState<string>("0");
  const [monthOvertime, setMonthOvertime] = useState<string>("0");
  const [todayHours, setTodayHours] = useState<string>("0");
  const [loading, setLoading] = useState(true);
  const [todayRecord, setTodayRecord] = useState<any>(null);
  const [isPunchDevice, setIsPunchDevice] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null); // Default to null for empty state
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [chartYear, setChartYear] = useState<number>(dayjs().year());
  const [employeeImage, setEmployeeImage] = useState<string | null>(null);
  const [attendanceChartData, setAttendanceChartData] = useState<any[]>([]);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  useEffect(() => {
    const fetchChartData = async () => {
      if (!employeeId || employeeId === "NaN" || !empToken) return;

      try {
        const chartData = [];
        for (let month = 1; month <= 12; month++) {
          const widgetData: AttendanceWidgetStats = await getAttendanceWidgets(
            month,
            chartYear,
            null,
            null,
            String(employeeId),
            empToken
          );

          const total = widgetData.Total_employee || 1;
          const present = widgetData.present || 0;
          const absent = widgetData.absent || 0;
          const late = widgetData.late || 0;

          const presentPercentage =
            total > 0 ? ((present / total) * 100).toFixed(1) : "0";
          const absentPercentage =
            total > 0 ? ((absent / total) * 100).toFixed(1) : "0";
          const latePercentage =
            total > 0 ? ((late / total) * 100).toFixed(1) : "0";

          chartData.push({
            month: monthNames[month - 1],
            present,
            absent,
            late,
            presentPercentage,
            absentPercentage,
            latePercentage,
          });
        }

        setAttendanceChartData(chartData);
      } catch (err: any) {
        console.error("Failed to fetch chart data", err);
      }
    };

    fetchChartData();
  }, [chartYear, employeeId, empToken]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadAttendance();
    loadWorkingHours();
  }, [selectedDate]);

  useEffect(() => {
    filterData();
  }, [attendanceData, searchText, selectedDate, selectedMonth, selectedYear]);

  useEffect(() => {
    if (employeeId && empToken) {
      fetchEmployeeImage(employeeId.toString(), empToken).then((imageUrl) => {
        setEmployeeImage(imageUrl);
      });
    }
  }, [employeeId, empToken]);

  const filterData = () => {
    let filtered = [...attendanceData];

    // Apply month filter for table data if a month is selected
    if (selectedMonth !== null) {
      filtered = filtered.filter((record) => {
        const dateValue =
          record.date ||
          record.check_in ||
          record.punch_in ||
          record.created_date ||
          record.attendance_date;
        if (!dateValue) return false;
        const recordDate = new Date(dateValue);
        return (
          recordDate.getMonth() === selectedMonth &&
          recordDate.getFullYear() === selectedYear
        );
      });
    } else {
      // Apply date filter for table data if no month is selected
      if (selectedDate) {
        const selectedDateStr = selectedDate.format("YYYY-MM-DD");
        filtered = filtered.filter((record) => {
          const dateValue =
            record.date ||
            record.check_in ||
            record.punch_in ||
            record.created_date ||
            record.attendance_date;
          if (!dateValue) return false;
          const recordDate = new Date(dateValue).toISOString().split("T")[0];
          return recordDate === selectedDateStr;
        });
      }
    }

    // Search filter
    if (searchText) {
      filtered = filtered.filter(
        (record) =>
          record.status?.toLowerCase().includes(searchText.toLowerCase()) ||
          record.source?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const records = await getAllAttendance(empToken);
      const filtered = records.filter((r) => {
        if (!employeeId || employeeId === "NaN") return false;

        const recordIdlStr = String(r.idl || "");
        const recordIdStr = String(r.id || "");
        const recordEmployeeIdStr = String(r.employee_id || "");
        const employeeIdStr = String(employeeId || "");
        const employeeIdFromStateStr = String(employeeIdFromState || "");

        if (recordEmployeeIdStr === employeeIdStr) return true;
        if (
          employeeIdFromState &&
          recordEmployeeIdStr === employeeIdFromStateStr
        )
          return true;

        if (recordIdlStr === employeeIdStr || recordIdStr === employeeIdStr)
          return true;
        if (
          employeeIdFromState &&
          (recordIdlStr === employeeIdFromStateStr ||
            recordIdStr === employeeIdFromStateStr)
        )
          return true;

        if (typeof employeeId === "number" && !isNaN(employeeId)) {
          if (r.idl === employeeId || r.id === employeeId) return true;
          if (
            !isNaN(Number(r.employee_id)) &&
            Number(r.employee_id) === employeeId
          )
            return true;
        }

        return false;
      });
      setAttendanceData(filtered || []);

      if (filtered?.length > 0) {
        const today = new Date().toISOString().split("T")[0];
        const todayRec = filtered.find((record) => {
          const recordDate = new Date(
            record.check_in || record.punch_in || record.date || ""
          )
            .toISOString()
            .split("T")[0];
          return recordDate === today;
        });

        if (todayRec) {
          setTodayRecord(todayRec);
          const isPunch = Boolean(todayRec.punch_in || todayRec.punch_out);
          setIsPunchDevice(isPunch);
          const hasSignedIn = Boolean(todayRec.check_in || todayRec.punch_in);
          const hasSignedOut = Boolean(
            todayRec.check_out || todayRec.punch_out
          );
          setSignedIn(hasSignedIn && !hasSignedOut);
        } else {
          setTodayRecord(null);
          setIsPunchDevice(false);
          setSignedIn(false);
        }
      } else {
        setTodayRecord(null);
        setIsPunchDevice(false);
        setSignedIn(false);
      }
    } catch (err: any) {
      console.error("Failed to load attendance", err);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkingHours = async () => {
    if (!employeeId || employeeId === "NaN" || !empToken) return;

    try {
      const day = selectedDate.date();
      const month = selectedDate.month() + 1;
      const year = selectedDate.year();

      const hoursData = await getAttendanceWorkingHours(
        String(employeeId),
        month,
        year,
        day,
        empToken
      );

      setTodayHours(hoursData.total_hours_today || "0");
      setWeekHours(hoursData.total_hours_week || "0");
      setMonthHours(hoursData.total_hours_month || "0");
      setMonthOvertime(hoursData.overtime_month || "0");
    } catch (err: any) {
      console.error("Failed to load working hours", err);
    }
  };

  const handleCheckIn = async () => {
    if (!employeeId || employeeId === "NaN") {
      console.error("Invalid Employee ID:", {
        employeeId,
        employeeIdFromState,
      });
      return toast.warning("Employee ID not available. Please login again.");
    }

    if (
      todayRecord &&
      isPunchDevice &&
      todayRecord.punch_in &&
      !todayRecord.punch_out
    ) {
      return toast.warning(
        "You checked in today using device, so you're not capable of online check-in."
      );
    }

    try {
      await postAttendanceSignIn(empToken);
      setSignedIn(true);
      loadAttendance();
      loadWorkingHours();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail === "Already signed in for the day.") {
        toast.warning(
          "You are already signed in for today. If you used a punch device, please use check-out to sign out."
        );
      } else {
        console.error("Check-in error:", err);
      }
    }
  };

  const handleCheckOut = async () => {
    if (!employeeId || employeeId === "NaN") {
      console.error("Invalid Employee ID:", {
        employeeId,
        employeeIdFromState,
      });
      return toast.warning("Employee ID not available. Please login again.");
    }

    if (
      todayRecord &&
      isPunchDevice &&
      todayRecord.punch_in &&
      !todayRecord.punch_out
    ) {
      return toast.warning(
        "You checked in today using device, so you're not capable of online check-out."
      );
    }

    try {
      await putAttendanceSignOut(empToken);
      setSignedIn(false);
      loadAttendance();
      loadWorkingHours();
    } catch (err: any) {
      console.error("Check-out error:", err);
    }
  };

  const formatTime = (time: string | null) => {
    if (!time) return "-";
    try {
      let date;
      if (time.includes("T")) {
        date = new Date(time);
      } else {
        date = new Date(`1970-01-01T${time}Z`);
      }
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  const formatDuration = (duration: number): string => {
    const hrs = Math.floor(duration / 3600000);
    const mins = Math.floor((duration % 3600000) / 60000);
    const secs = Math.floor((duration % 60000) / 1000);
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getLiveWorkingTime = (
    checkIn: string | null,
    status: string
  ): string => {
    if (!checkIn || status === "Absent") return "-";
    const start = new Date(checkIn).getTime();
    const now = Date.now();
    const diff = now - start;
    return formatDuration(diff);
  };

  const isWeekOff = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0;
  };

  const getColumns = (): any[] => [
    {
      title: "Date",
      key: "date",
      width: 100,
      align: "center" as const,
      render: (record: any) => {
        const dateValue =
          record.date ||
          record.check_in ||
          record.punch_in ||
          record.created_date ||
          record.attendance_date;

        if (!dateValue) return "-";

        const dateObj = new Date(dateValue);
        const formattedDate = dateObj.toLocaleDateString("en-GB");
        const dayShort = dateObj
          .toLocaleDateString("en-US", { weekday: "short" })
          .slice(0, 3);

        const badgeColor = isWeekOff(dateObj)
          ? "#fd7e14"
          : dayShort === "Sat"
          ? "#fd0d91ff"
          : "#1763AD";

        return (
          <div className="text-center d-flex flex-column align-items-center gap-1">
            <div className="text-dark">
              <b>{formattedDate}</b>
            </div>
            <span
              className="badge"
              style={{
                backgroundColor: badgeColor,
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
              }}
            >
              {dayShort}
            </span>
          </div>
        );
      },
    },
    {
      title: "Check In & Out",
      key: "check",
      width: 130,
      align: "center" as const,
      render: (record: any) => {
        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;

        const getBadge = (iconClass: string, timeValue: string | null) => (
          <div
            className={`badge ${
              iconClass.includes("logout")
                ? "badge bg-danger-subtle text-danger"
                : "badge bg-success-subtle text-success"
            }`}
            style={{
              fontSize: "10px",
              padding: "6px 10px",
              width: "100px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className={`ti ${iconClass}`} style={{ fontSize: "12px" }} />
            <span style={{ fontSize: "11px", fontWeight: 500 }}>
              {timeValue ? formatTime(timeValue) : "Pending"}
            </span>
          </div>
        );

        return (
          <div className="d-flex flex-column align-items-center gap-1">
            {getBadge(isPunch ? "ti-fingerprint" : "ti-device-laptop", checkIn)}
            {getBadge("ti-logout", checkOut)}
          </div>
        );
      },
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      width: 110,
      align: "center" as const,
      render: (_: any, record: any) => {
        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;
        const status = record.status;

        const hasCheckedOut = Boolean(checkOut);
        const storedHours = record.working_hours;

        const displayTime =
          status === "Absent"
            ? "-"
            : hasCheckedOut
            ? storedHours || "-"
            : checkIn
            ? getLiveWorkingTime(checkIn, status)
            : "-";

        return (
          <span className="text-dark">
            <b>{displayTime}</b>
          </span>
        );
      },
    },
    {
      title: "Production Hours",
      dataIndex: "production_hr",
      width: 100,
      align: "center" as const,
      render: (hours: string) => (
        <span className="text-dark">{hours || "-"}</span>
      ),
    },
    {
      title: "Overtime",
      dataIndex: "over_time",
      width: 90,
      align: "center" as const,
      render: (overtime: string) => (
        <span className="text-dark">{overtime || "-"}</span>
      ),
    },
    {
      title: "Break",
      dataIndex: "Break",
      width: 80,
      align: "center" as const,
      render: () => (
        <span className="d-inline-flex align-items-center">
          <span
            className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: "12px" }} />
          </span>
          1 hr
        </span>
      ),
    },
    {
      title: "Late/Early",
      key: "late_early",
      width: 90,
      align: "center" as const,
      render: (_: any, record: any) => (
        <div>
          <div
            style={{ fontSize: "11px", color: "#dc3545", fontWeight: "500" }}
          >
            {record.late || record.LateComing || "--"}
          </div>
          <div
            style={{ fontSize: "11px", color: "#28a745", fontWeight: "500" }}
          >
            {record.earlyout || record.EarlyGoing || "--"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      align: "center" as const,
      render: (status: string) => {
        let className = "badge bg-light-transparent";
        if (status === "Present") className = "badge bg-success-transparent";
        else if (status?.includes("Half"))
          className = "badge bg-warning-transparent";
        else if (status === "Absent") className = "badge bg-danger-transparent";
        else if (status === "Late") className = "badge bg-pink-transparent";
        else if (status === "Early") className = "badge bg-purple-transparent";

        return (
          <span
            className={className}
            style={{
              fontSize: "12px",
              fontWeight: "500",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {status || "-"}
          </span>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      width: 80,
      align: "center" as const,
      render: (source: string) => (
        <span className="text-dark">{source || "-"}</span>
      ),
    },
  ];

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    if (hour < 20) return "Good Evening";
    return "Good Night";
  };

  if (!employeeId || employeeId === "NaN") {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">
            <h5>Error: Employee ID not found</h5>
            <p>Please login again to access the attendance page.</p>
            <p>
              Debug Info: employeeId = {String(employeeId)}, state ={" "}
              {String(employeeIdFromState)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Self Attendance</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.employeeDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Attendance</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <ReactDatePicker
              selected={selectedDate ? selectedDate.toDate() : null}
              onChange={(date) => setSelectedDate(dayjs(date))}
              customInput={
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  style={{ maxHeight: "34px", minWidth: "130px" }}
                >
                  <i className="ti ti-calendar me-2" />
                  {selectedDate
                    ? selectedDate.format("DD-MM-YYYY")
                    : "Select Date"}
                </button>
              }
              dateFormat="dd-MM-yyyy"
              popperPlacement="bottom-start"
            />
            <div>
              <CollapseHeader />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-4 d-flex">
            <div className="card flex-fill border-primary attendance-bg">
              <div className="card-body">
                <div className="mb-4 text-center">
                  <h6 className="fw-medium text-gray-5 mb-2">
                    {getGreeting()}, {name}
                  </h6>
                  <h4 className="mb-3">
                    {currentTime.toLocaleTimeString()} –{" "}
                    {currentTime.toDateString()}
                  </h4>
                  <div className="small text-muted">
                    Employee ID: {employeeId}
                  </div>
                  <div
                    className="avatar avatar-rounded my-3 mx-auto"
                    style={{
                      width: "130px",
                      height: "130px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={employeeImage || "/assets/img/defaultavatar.png"}
                      alt="/assets/img/defaultavatar.png"
                    />
                  </div>
                  <h6 className="fw-medium mb-3">
                    <i className="ti ti-fingerprint text-primary me-2" />
                    {signedIn ? "Checked In" : "Not Checked In"}
                  </h6>
                  {isPunchDevice && todayRecord && (
                    <div
                      className="alert alert-info p-2 mb-3"
                      style={{ fontSize: "12px" }}
                    >
                      <i className="ti ti-info-circle me-1" />
                      You used punch device today
                    </div>
                  )}
                  {!signedIn ? (
                    <button
                      onClick={handleCheckIn}
                      className="btn btn-success w-100"
                      disabled={isPunchDevice && todayRecord}
                    >
                      Check In
                    </button>
                  ) : (
                    <button
                      onClick={handleCheckOut}
                      className="btn btn-danger w-100"
                      disabled={isPunchDevice && todayRecord}
                    >
                      Check Out
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-8 d-flex">
            <div className="row flex-fill">
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-0">
                      <span className="avatar avatar-sm bg-primary mb-2">
                        <i className="ti ti-clock-stop" />
                      </span>
                      <h2 className="mb-2">
                        {todayHours} /{" "}
                        <span className="fs-20 text-gray-5"> 8</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Today
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-0">
                      <span className="avatar avatar-sm bg-dark mb-2">
                        <i className="ti ti-clock-up" />
                      </span>
                      <h2 className="mb-2">
                        {weekHours} /{" "}
                        <span className="fs-20 text-gray-5"> 48</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Week
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-0">
                      <span className="avatar avatar-sm bg-info mb-2">
                        <i className="ti ti-calendar-up" />
                      </span>
                      <h2 className="mb-2">
                        {monthHours} /{" "}
                        <span className="fs-20 text-gray-5">208</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Month
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-0">
                      <span className="avatar avatar-sm bg-pink mb-2">
                        <i className="ti ti-calendar-star" />
                      </span>
                      <h2 className="mb-2">{monthOvertime}</h2>
                      <p className="fw-medium text-truncate">
                        Overtime this Month
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Attendance Overview</h5>
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-label fw-semibold mb-0 me-2">
                          Year:
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={chartYear}
                          onChange={(e) => setChartYear(+e.target.value)}
                          min="2020"
                          max="2030"
                          style={{ width: "90px" }}
                        />
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={attendanceChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="month"
                          stroke="#888888"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#888888"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value: any) => `${value}%`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#111827",
                            borderRadius: "6px",
                            border: "none",
                            color: "#fff",
                          }}
                          labelStyle={{ color: "#fff" }}
                          formatter={(value: any, name: any, props: any) => {
                            if (name === "present") {
                              return [
                                `${props.payload.present} days (${value}%)`,
                                "Present",
                              ];
                            } else if (name === "absent") {
                              return [
                                `${props.payload.absent} days (${value}%)`,
                                "Absent",
                              ];
                            } else if (name === "late") {
                              return [
                                `${props.payload.late} days (${value}%)`,
                                "Late",
                              ];
                            }
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="presentPercentage"
                          name="Present"
                          stroke="#22c55e"
                          strokeWidth={3}
                          dot={{ r: 5, fill: "#22c55e" }}
                          activeDot={{ r: 8 }}
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="absentPercentage"
                          name="Absent"
                          stroke="#ef4444"
                          strokeWidth={3}
                          dot={{ r: 5, fill: "#ef4444" }}
                          activeDot={{ r: 8 }}
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="latePercentage"
                          name="Late"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={{ r: 5, fill: "#f5e040ff" }}
                          activeDot={{ r: 8 }}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h5 className="mb-0">My Attendance Records</h5>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-end gap-2">
                  <Dropdown className="ms-auto">
                    <Dropdown.Toggle
                      variant="light"
                      className="border w-100 text-start"
                      style={{ height: "35px" }}
                    >
                      {selectedMonth !== null
                        ? `${monthNames[selectedMonth]} - ${selectedYear}`
                        : "Select Month"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2 dropdown-menu-end">
                      <div className="d-flex flex-wrap">
                        {monthNames.map((month, idx) => (
                          <button
                            key={month}
                            onClick={() => setSelectedMonth(idx)}
                            className="btn btn-outline-primary btn-sm m-1"
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2">
                        <input
                          type="number"
                          className="form-control"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(+e.target.value)}
                        />
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>

                  <Input.Search
                    placeholder="Search records..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300, height: "45px" }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <Spin spinning={loading}>
              <Table
                dataSource={filteredData}
                columns={getColumns()}
                rowKey="id"
                scroll={{ x: 1200 }}
                size="small"
                className="table-responsive"
              />
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceAdmin;
